/**
 * GapInterviewModal
 *
 * Opens a short Gemini Live voice interview focused on a specific resume gap.
 * After the user ends the interview, the transcript is summarized by an LLM
 * into a structured career memory which the user can review and save.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';
import { useGeminiLive } from '../../hooks/useGeminiLive';
import { useLanguage } from '../../contexts/LanguageContext';
import { createCareerExperience } from '../../services/careerMemory';
import './GapInterviewModal.css';

const MIC_DEVICE_STORAGE_KEY = 'postmortem-gap-interview-device-id';

interface Props {
  requirement: string;
  strength: 'gap' | 'partial';
  evidence?: string;
  interviewQuestions?: string[];
  onSaved: () => void;
  onClose: () => void;
}

type Phase = 'ready' | 'voice' | 'processing' | 'review' | 'saved';

interface Summary {
  shouldSave: boolean;
  title: string;
  description: string;
  rationale: string;
  assessment: 'closes_gap' | 'partial_improvement' | 'no_change';
}

const ASSESSMENT_META: Record<Summary['assessment'], { label: string }> = {
  closes_gap:          { label: '✓ Closes the gap' },
  partial_improvement: { label: '~ Partial improvement' },
  no_change:           { label: '✗ Gap remains' },
};

export function GapInterviewModal({ requirement, strength, evidence, interviewQuestions, onSaved, onClose }: Props) {
  const { language } = useLanguage();
  const [phase, setPhase] = useState<Phase>('ready');
  const [summary, setSummary] = useState<Summary | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>(
    () => localStorage.getItem(MIC_DEVICE_STORAGE_KEY) ?? ''
  );

  // Accumulated evidence from previous rounds — passed to the context function
  // so the coach knows what was already discovered before this session.
  const [currentEvidence, setCurrentEvidence] = useState<string>(evidence || '');
  const [currentStrength, setCurrentStrength] = useState<typeof strength>(strength);
  const [roundCount, setRoundCount] = useState(0);

  const openingQuestion = interviewQuestions?.[0];
  const initialPrompt = roundCount > 0
    ? `[This is a follow-up interview. Some evidence was already captured: "${currentEvidence}". Acknowledge this briefly, then ask if the user has more specific examples or context to add.]`
    : openingQuestion
      ? `[Start the gap interview. Greet the user briefly, then ask: "${openingQuestion}"]`
      : `[Start the gap interview. Greet the user briefly and ask whether they have experience with "${requirement}", even indirect or partial.]`;

  const PAUSE_PRESETS = [
    { label: 'Quick', ms: 600 },
    { label: 'Normal', ms: 800 },
    { label: 'Relaxed', ms: 1500 },
  ] as const;

  const [pauseMs, setPauseMs] = useState<number>(600);

  const {
    state: voiceState,
    connect,
    disconnect,
    transcript,
    error: voiceError,
    rawMicLevel,
    micDeviceName,
    availableDevices,
    refreshDevices,
    setSilenceDuration,
  } = useGeminiLive(
    language,
    undefined,
    {
      contextFunction: 'getGapInterviewContext',
      contextParams: { requirement, strength: currentStrength, evidence: currentEvidence },
      skipChatHistory: true,
      initialPrompt,
    }
  );

  useEffect(() => {
    refreshDevices();
  }, [refreshDevices]);

  const handleDeviceSelect = (deviceId: string) => {
    setSelectedDeviceId(deviceId);
    localStorage.setItem(MIC_DEVICE_STORAGE_KEY, deviceId);
  };

  const handlePauseSelect = (ms: number) => {
    setPauseMs(ms);
    setSilenceDuration(ms);
  };

  const handleStart = () => {
    setSilenceDuration(pauseMs); // ensure latest value is applied
    setPhase('voice');
    connect(selectedDeviceId || undefined);
  };

  const handleEndInterview = useCallback(async () => {
    await disconnect();
    if (transcript.length === 0) {
      onClose();
      return;
    }
    setPhase('processing');
    try {
      const summarize = httpsCallable<any, Summary>(functions, 'summarizeGapInterview');
      // Filter out system marker entries (pause/interrupted indicators) — only send speech content
      const speechTranscript = transcript
        .filter(e => e.role !== 'system')
        .map(e => ({ role: e.role, text: e.text }));
      const result = await summarize({
        requirement,
        strength,
        transcript: speechTranscript,
      });
      setSummary(result.data);
    } catch (err: any) {
      setError('Could not analyze the interview. ' + (err.message || 'Please try again.'));
    } finally {
      setPhase('review');
    }
  }, [disconnect, onClose, requirement, strength, transcript]);

  const handleSave = async () => {
    if (!summary?.shouldSave) return;
    setSaving(true);
    try {
      await createCareerExperience({
        title: summary.title,
        description: summary.description,
        tags: ['resume-gap', 'gap-fill', summary.assessment],
      });
      onSaved(); // notify parent (marks row as saved) — does NOT close modal
      // Update evidence for the next round so the coach knows what was already found
      setCurrentEvidence(prev => [prev, summary.description].filter(Boolean).join('\n\n---\n'));
      setCurrentStrength('partial');
      setPhase('saved');
    } catch (err: any) {
      setError('Failed to save: ' + (err.message || 'unknown error'));
      setSaving(false);
    }
  };

  const handleComplement = () => {
    setSummary(null);
    setError('');
    setRoundCount(r => r + 1);
    setPhase('ready');
  };

  // Always disconnect the voice session before closing the modal.
  // If the user closes via X or Cancel while a session is active, without this
  // the WebSocket keeps running in the background with no UI to stop it.
  const handleClose = useCallback(() => {
    disconnect();
    onClose();
  }, [disconnect, onClose]);

  // Level quality class for CSS-driven colour (green/yellow/red)
  const levelQuality = rawMicLevel > 30 ? 'high' : rawMicLevel > 10 ? 'medium' : 'low';

  // VAD debug: track client-side silence duration as proxy for Gemini's VAD state.
  // rawMicLevel updates every ~100ms from the AnalyserNode.
  // Hysteresis: require a higher level to enter "speaking" and a lower level to leave it,
  // preventing the indicator from flickering when mic level oscillates near the threshold.
  const SPEECH_THRESHOLD = 8;   // rawMicLevel must reach THIS to enter speaking mode
  const SILENCE_THRESHOLD = 4;  // rawMicLevel must drop BELOW this to leave speaking mode
  const silenceStartRef = useRef<number | null>(null);
  const isSpeakingRef = useRef(false);
  const [silenceMs, setSilenceMs] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);

  useEffect(() => {
    if (voiceState !== 'active') {
      silenceStartRef.current = null;
      isSpeakingRef.current = false;
      setSilenceMs(0);
      setIsSpeaking(false);
      return;
    }
    if (!isSpeakingRef.current && rawMicLevel >= SPEECH_THRESHOLD) {
      // Transition: silence → speaking
      isSpeakingRef.current = true;
      silenceStartRef.current = null;
      setSilenceMs(0);
      setIsSpeaking(true);
    } else if (isSpeakingRef.current && rawMicLevel < SILENCE_THRESHOLD) {
      // Transition: speaking → silence
      isSpeakingRef.current = false;
      if (!silenceStartRef.current) silenceStartRef.current = Date.now();
      setSilenceMs(Date.now() - silenceStartRef.current);
      setIsSpeaking(false);
    } else if (!isSpeakingRef.current && rawMicLevel < SILENCE_THRESHOLD) {
      // Already in silence — keep updating the timer
      if (!silenceStartRef.current) silenceStartRef.current = Date.now();
      setSilenceMs(Date.now() - silenceStartRef.current);
    }
    // If speaking and rawMicLevel is between SILENCE_THRESHOLD and SPEECH_THRESHOLD:
    // do nothing — maintain current state (hysteresis zone).
  }, [rawMicLevel, voiceState]);

  // Update CSS custom property for level bar width without inline styles
  const meterRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    meterRef.current?.style.setProperty('--gap-meter-level', String(rawMicLevel));
  }, [rawMicLevel]);

  return (
    <div className="gap-interview-overlay">
      <div className="gap-interview-modal" role="dialog" aria-modal="true">

        {/* Header */}
        <div className="gap-interview-header">
          <div className="gap-interview-header-text">
            <h3>Gap Interview <span style={{fontSize:'10px',opacity:0.4}}>v5</span></h3>
            <p>{requirement}</p>
          </div>
          <button type="button" className="gap-interview-close" onClick={handleClose} aria-label="Close">×</button>
        </div>

        {/* Body */}
        <div className="gap-interview-body">

          {/* Phase: ready */}
          {phase === 'ready' && (
            <div className="gap-interview-ready">
              <div className="gap-interview-icon">🎙</div>
              <p className="gap-interview-intro">
                {strength === 'gap'
                  ? `Your career memory has no evidence for "${requirement}". A short voice interview might surface experience you haven't captured yet.`
                  : `Your career memory has partial evidence for "${requirement}". Let's dig deeper with a quick voice conversation.`}
              </p>
              {interviewQuestions && interviewQuestions.length > 0 && (
                <div className="gap-prepared-questions">
                  <p className="gap-prepared-questions-label">We'll explore:</p>
                  <ol className="gap-prepared-questions-list">
                    {interviewQuestions.map((q, i) => (
                      <li key={i}>{q}</li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Pause sensitivity */}
              <div className="gap-mic-selector">
                <p className="gap-mic-selector-label">Response speed</p>
                <div className="gap-mic-device-list">
                  {PAUSE_PRESETS.map((preset) => (
                    <button
                      type="button"
                      key={preset.ms}
                      className={`gap-mic-device-btn${pauseMs === preset.ms ? ' gap-mic-device-btn-active' : ''}`}
                      onClick={() => handlePauseSelect(preset.ms)}
                    >
                      <span className="gap-mic-device-icon">
                        {pauseMs === preset.ms ? '◉' : '○'}
                      </span>
                      <span className="gap-mic-device-label">{preset.label}</span>
                    </button>
                  ))}
                </div>
                <p className="gap-pause-hint">
                  How long the AI waits after you stop speaking before responding
                </p>
              </div>

              {/* Microphone selection */}
              <div className="gap-mic-selector">
                <p className="gap-mic-selector-label">Microphone</p>
                {availableDevices.length > 0 ? (
                  <div className="gap-mic-device-list">
                    {availableDevices.map((device) => (
                      <button
                        type="button"
                        key={device.deviceId}
                        className={`gap-mic-device-btn${selectedDeviceId === device.deviceId ? ' gap-mic-device-btn-active' : ''}`}
                        onClick={() => handleDeviceSelect(device.deviceId)}
                      >
                        <span className="gap-mic-device-icon">
                          {selectedDeviceId === device.deviceId ? '◉' : '○'}
                        </span>
                        <span className="gap-mic-device-label">{device.label}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="gap-mic-loading">Loading devices…</p>
                )}
              </div>

              <p className="gap-interview-hint">
                The AI coach will ask you 3–5 questions. It takes about 2–3 minutes. At the end, it will synthesize your answers into a career memory.
              </p>
              <button type="button" className="btn btn-primary gap-interview-start" onClick={handleStart}>
                Start voice interview
              </button>
            </div>
          )}

          {/* Phase: voice */}
          {phase === 'voice' && (
            <div className="gap-interview-voice">
              {voiceState === 'connecting' && (
                <div className="gap-interview-spinner" />
              )}
              {voiceState === 'active' && (
                <div className="gap-interview-pulse-wrapper">
                  <div className="gap-interview-pulse" />
                  <div className="gap-interview-pulse-ring" />
                </div>
              )}
              {voiceState === 'error' && (
                <p className="gap-interview-error">{voiceError || 'Voice connection failed.'}</p>
              )}
              <p className="gap-interview-voice-status">
                {voiceState === 'connecting' && 'Connecting…'}
                {voiceState === 'active' && 'Listening — speak naturally'}
                {voiceState === 'error' && ''}
              </p>

              {/* Audio level meter — only when active */}
              {voiceState === 'active' && (
                <div
                  ref={meterRef}
                  className="gap-audio-meter"
                  data-quality={levelQuality}
                >
                  <div className="gap-level-bar-row">
                    <span className="gap-level-label">Mic</span>
                    <div className="gap-level-bar-track">
                      <div className={`gap-level-bar-fill gap-level-bar-fill--${levelQuality}`} />
                    </div>
                    <span className="gap-level-pct">{rawMicLevel}%</span>
                  </div>
                  <div className="gap-spectrometer">
                    {Array.from({ length: 20 }, (_, i) => {
                      const threshold = (i + 1) * 5;
                      const barQuality = threshold > 60 ? 'high' : threshold > 30 ? 'medium' : 'low';
                      return (
                        <div
                          key={i}
                          className={`gap-spectrometer-bar gap-spectrometer-bar--pos${i + 1}`}
                          data-active={rawMicLevel >= threshold ? 'true' : 'false'}
                          data-quality={barQuality}
                        />
                      );
                    })}
                  </div>
                  {micDeviceName && (
                    <div className="gap-mic-device-name">{micDeviceName}</div>
                  )}
                  {/* VAD debug indicator */}
                  <div className={`gap-vad-debug gap-vad-debug--${isSpeaking ? 'speaking' : silenceMs > pauseMs ? 'triggered' : 'silent'}`}>
                    <span className="gap-vad-dot" />
                    {isSpeaking
                      ? 'Speaking'
                      : silenceMs >= pauseMs
                        ? `Silent: ${silenceMs}ms ← VAD trigger (${pauseMs}ms)`
                        : `Silent: ${silenceMs}ms / ${pauseMs}ms`
                    }
                  </div>
                </div>
              )}

              {transcript.length > 0 && (
                <div className="gap-interview-transcript">
                  {transcript.map((entry, i) => (
                    entry.role === 'system' ? (
                      <div key={i} className="gap-transcript-marker">
                        {entry.text}
                      </div>
                    ) : (
                      <div key={i} className={`gap-transcript-entry gap-transcript-${entry.role}`}>
                        <span className="gap-transcript-role">
                          {entry.role === 'user' ? 'You' : 'Coach'}
                        </span>
                        {entry.text}
                      </div>
                    )
                  ))}
                </div>
              )}

              <div className="gap-interview-voice-actions">
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleEndInterview}
                  disabled={voiceState === 'connecting'}
                >
                  Done — analyse my responses
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={handleClose}
                  title="End conversation without saving"
                >
                  End this conversation
                </button>
              </div>
            </div>
          )}

          {/* Phase: processing */}
          {phase === 'processing' && (
            <div className="gap-interview-processing">
              <div className="gap-interview-spinner" />
              <p>Analysing your responses…</p>
            </div>
          )}

          {/* Phase: review */}
          {phase === 'review' && (
            <div className="gap-interview-review">
              {error && <p className="gap-interview-error">{error}</p>}
              {summary && (
                <>
                  <div className={`gap-review-assessment gap-review-assessment--${summary.assessment}`}>
                    {ASSESSMENT_META[summary.assessment].label}
                  </div>
                  {summary.rationale && (
                    <p className="gap-review-rationale">{summary.rationale}</p>
                  )}
                  {summary.shouldSave ? (
                    <>
                      <h4 className="gap-review-title">{summary.title}</h4>
                      <p className="gap-review-description">{summary.description}</p>
                      <p className="gap-review-hint">
                        Review the memory above. If it looks right, save it — it will be included in future Deep Tailor runs.
                      </p>
                      <div className="gap-review-actions">
                        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
                          {saving ? 'Saving…' : 'Save to career memory'}
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={saving}>
                          Dismiss
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="gap-review-no-save">
                        Based on our conversation, there wasn't enough specific evidence to add a new memory. That's okay — you can do another round if you think of more to add.
                      </p>
                      <div className="gap-review-actions">
                        <button type="button" className="btn btn-primary" onClick={handleComplement}>
                          Try another round
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={handleClose}>Close</button>
                      </div>
                    </>
                  )}
                </>
              )}
              {!summary && !error && (
                <button type="button" className="btn btn-secondary" onClick={handleClose}>Close</button>
              )}
            </div>
          )}

          {/* Phase: saved */}
          {phase === 'saved' && summary && (
            <div className="gap-interview-review">
              <div className="gap-review-saved-badge">✓ Saved to career memory</div>
              <h4 className="gap-review-title">{summary.title}</h4>
              <p className="gap-review-description">{summary.description}</p>
              {summary.rationale && (
                <p className="gap-review-rationale">{summary.rationale}</p>
              )}
              <p className="gap-review-hint">
                Want to add more context or examples for this requirement? Do another round — it will create an additional memory entry.
              </p>
              <div className="gap-review-actions">
                <button type="button" className="btn btn-primary" onClick={handleComplement}>
                  Complement with another interview
                </button>
                <button type="button" className="btn btn-secondary" onClick={handleClose}>
                  Done
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
